import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Check if we're in production environment
export const isProduction = () => {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  // Check for venndela.com production (not dev. or stg. subdomain)
  return apiUrl.includes('api.venndela.com') && !apiUrl.includes('api.dev.') && !apiUrl.includes('api.stg.');
};

// Feature flags
export const features = {
  marketplace: !isProduction(),
};

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { tokens, currentOrganization } = useAuthStore.getState();

    if (tokens?.idToken) {
      config.headers.Authorization = `Bearer ${tokens.idToken}`;
    }

    if (currentOrganization) {
      config.headers['X-Organization-Id'] = currentOrganization.organizationId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { tokens, setTokens, logout } = useAuthStore.getState();

        if (tokens?.refreshToken) {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refreshToken: tokens.refreshToken,
          });

          if (response.data.success) {
            setTokens(response.data.data.tokens);
            originalRequest.headers.Authorization = `Bearer ${response.data.data.tokens.idToken}`;
            return api(originalRequest);
          }
        }

        logout();
      } catch {
        useAuthStore.getState().logout();
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  logout: () => api.post('/auth/logout'),

  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    accountType: 'affiliate' | 'organization';
    organizationName?: string;
    businessCategory?: string;
    acceptedTerms: boolean;
    orgSlug?: string; // Optional: join this org's affiliate program during registration
  }) => api.post('/auth/register', data),

  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  verifyEmail: (email: string, code: string) =>
    api.post('/auth/verify-email', { email, code }),

  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', { email }),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (email: string, code: string, newPassword: string) =>
    api.post('/auth/reset-password', { email, code, newPassword }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  getMe: () => api.get('/auth/me'),

  updateMe: (data: { firstName?: string; lastName?: string }) =>
    api.put('/auth/me', data),
};

// Organizations API
export const organizationsApi = {
  list: () => api.get('/organizations'),
  get: (id: string) => api.get(`/organizations/${id}`),
  getBySlug: (slug: string) => api.get(`/organizations/slug/${slug}`),
  create: (data: { name: string; website?: string }) =>
    api.post('/organizations', data),
  update: (id: string, data: Partial<{ name: string; website: string; settings: object }>) =>
    api.put(`/organizations/${id}`, data),
  getStats: (id: string) => api.get(`/organizations/${id}/stats`),
  regenerateApiKey: (id: string) =>
    api.post(`/organizations/${id}/api-key`),
  connectStripe: (id: string) =>
    api.post(`/organizations/${id}/stripe/connect`),
  connectStripeOAuth: (id: string) =>
    api.post(`/organizations/${id}/stripe/oauth`),
  stripeCallback: (id: string) =>
    api.get(`/organizations/${id}/stripe/callback`),
};

// Affiliate Invites API
export const invitesApi = {
  // Org admin endpoints
  create: (data: { email?: string; expiresInDays?: number }) =>
    api.post('/organizations/invites', data),
  list: (params?: { status?: string }) =>
    api.get('/organizations/invites', { params }),
  revoke: (inviteId: string) =>
    api.delete(`/organizations/invites/${inviteId}`),

  // Public endpoints
  getByCode: (inviteCode: string) =>
    api.get(`/invites/${inviteCode}`),
  accept: (inviteCode: string) =>
    api.post(`/invites/${inviteCode}/accept`),
};

// Affiliates API
export const affiliatesApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/affiliates', { params }),
  get: (id: string) => api.get(`/affiliates/${id}`),
  apply: (organizationSlug: string) =>
    api.post('/affiliates/apply', { organizationSlug }),
  join: (organizationSlug: string, applicationNote?: string) =>
    api.post('/affiliates/join', { organizationSlug, applicationNote }),
  approve: (id: string) => api.post(`/affiliates/${id}/approve`),
  reject: (id: string, reason?: string) =>
    api.post(`/affiliates/${id}/reject`, { reason }),
  suspend: (id: string, reason?: string) =>
    api.post(`/affiliates/${id}/suspend`, { reason }),
  update: (id: string, data: object) =>
    api.put(`/affiliates/${id}`, data),
  getMe: () => api.get('/affiliates/me'),
  updateMe: (data: object) => api.put('/affiliates/me', data),
  getMyStats: () => api.get('/affiliates/me/stats'),
  // Stripe Connect (Express) for affiliates
  connectStripe: () => api.post('/affiliates/me/stripe/connect'),
  getStripeStatus: () => api.get('/affiliates/me/stripe/status'),
  // Withdrawal system
  getWithdrawalInfo: () => api.get('/affiliates/me/withdrawal-info'),
  requestWithdrawal: (data: { amount?: number; paymentMethod: string }) =>
    api.post('/affiliates/me/withdrawal-request', data),
  // Stripe Bank (Custom account) for direct bank payouts
  setupStripeBank: (country?: string) =>
    api.post('/affiliates/me/stripe-bank/setup', { country }),
  getStripeBankStatus: () => api.get('/affiliates/me/stripe-bank/status'),
  // Payment method preferences
  setDefaultPaymentMethod: (method: string) =>
    api.put('/affiliates/me/payment-methods/default', { method }),
};

// Links API
export const linksApi = {
  list: () => api.get('/affiliates/me/links'),
  create: (data: { destinationUrl: string; name?: string; campaignId?: string }) =>
    api.post('/affiliates/me/links', data),
  get: (id: string) => api.get(`/affiliates/me/links/${id}`),
  update: (id: string, data: { name?: string; destinationUrl?: string }) =>
    api.put(`/affiliates/me/links/${id}`, data),
  delete: (id: string) => api.delete(`/affiliates/me/links/${id}`),
};

// Campaigns API
export const campaignsApi = {
  list: (params?: { status?: string }) =>
    api.get('/campaigns', { params }),
  get: (id: string) => api.get(`/campaigns/${id}`),
  create: (data: {
    name: string;
    description?: string;
    type: string;
    commissionValue: number;
    isDefault?: boolean;
  }) => api.post('/campaigns', data),
  update: (id: string, data: object) =>
    api.put(`/campaigns/${id}`, data),
  delete: (id: string) => api.delete(`/campaigns/${id}`),
};

// Commissions API
export const commissionsApi = {
  listMine: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/my/commissions', { params }),
  list: (params?: { status?: string; affiliateId?: string; page?: number; limit?: number }) =>
    api.get('/commissions', { params }),
  get: (id: string) => api.get(`/commissions/${id}`),
  approve: (id: string) => api.post(`/commissions/${id}/approve`),
  reject: (id: string, reason?: string) =>
    api.post(`/commissions/${id}/reject`, { reason }),
  bulkApprove: (ids: string[]) =>
    api.post('/commissions/bulk-approve', { commissionIds: ids }),
};

// Payouts API
export const payoutsApi = {
  listMine: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/payouts/mine', { params }),
  list: (params?: { status?: string; affiliateId?: string; page?: number; limit?: number }) =>
    api.get('/payouts', { params }),
  get: (id: string) => api.get(`/payouts/${id}`),
  getPayable: () => api.get('/payouts/payable'),
  create: (affiliateId: string) =>
    api.post('/payouts', { affiliateId }),
  process: (id: string) => api.post(`/payouts/${id}/process`),
  completeManual: (id: string) => api.post(`/payouts/${id}/complete-manual`),
};

// Admin API
export const adminApi = {
  getPlatformStats: () => api.get('/admin/stats'),

  listOrganizations: (params?: { status?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/admin/organizations', { params }),
  getOrganization: (id: string) => api.get(`/admin/organizations/${id}`),
  updateOrganization: (id: string, data: object) =>
    api.put(`/admin/organizations/${id}`, data),
  suspendOrganization: (id: string, reason?: string) =>
    api.post(`/admin/organizations/${id}/suspend`, { reason }),
  activateOrganization: (id: string) =>
    api.post(`/admin/organizations/${id}/activate`),

  listUsers: (params?: { role?: string; organizationId?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/admin/users', { params }),
  getUser: (id: string) => api.get(`/admin/users/${id}`),
  updateUser: (id: string, data: object) =>
    api.put(`/admin/users/${id}`, data),
  createSuperadmin: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => api.post('/admin/users/superadmin', data),

  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data: object) => api.put('/admin/settings', data),

  listAllCommissions: (params?: { status?: string; organizationId?: string; page?: number; limit?: number }) =>
    api.get('/admin/commissions', { params }),
  listAllPayouts: (params?: { status?: string; organizationId?: string; page?: number; limit?: number }) =>
    api.get('/admin/payouts', { params }),

  // Payout approval system
  getPendingApprovalPayouts: () => api.get('/admin/payouts/pending-approval'),
  approvePayouts: (payoutIds: string[]) =>
    api.post('/admin/payouts/approve', { payoutIds }),
  rejectPayout: (payoutId: string, reason: string) =>
    api.post('/admin/payouts/reject', { payoutId, reason }),
  getPayoutSummary: () => api.get('/admin/payouts/summary'),

  // Catalog management
  listCatalogListings: (params?: { status?: string; tier?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/admin/catalog', { params }),
  getCatalogListing: (listingId: string) =>
    api.get(`/admin/catalog/${listingId}`),
  updateCatalogListing: (listingId: string, data: { status?: string; tier?: string; adminNotes?: string }) =>
    api.put(`/admin/catalog/${listingId}`, data),
  approveCatalogListing: (listingId: string) =>
    api.post(`/admin/catalog/${listingId}/approve`),
  suspendCatalogListing: (listingId: string, reason?: string) =>
    api.post(`/admin/catalog/${listingId}/suspend`, { reason }),

  // Stripe management
  listStripeProducts: (params?: { active?: boolean; limit?: number }) =>
    api.get('/admin/stripe/products', { params }),
  createStripeProduct: (data: {
    name: string;
    description?: string;
    metadata?: Record<string, string>;
    images?: string[];
  }) => api.post('/admin/stripe/products', data),
  updateStripeProduct: (productId: string, data: {
    name?: string;
    description?: string;
    active?: boolean;
    metadata?: Record<string, string>;
    images?: string[];
    defaultPriceId?: string;
  }) => api.put(`/admin/stripe/products/${productId}`, data),
  listStripePrices: (params?: { productId?: string; active?: boolean; type?: string; limit?: number }) =>
    api.get('/admin/stripe/prices', { params }),
  createStripePrice: (data: {
    productId: string;
    unitAmount: number;
    currency?: string;
    nickname?: string;
    recurring?: {
      interval: 'day' | 'week' | 'month' | 'year';
      intervalCount?: number;
    };
    metadata?: Record<string, string>;
  }) => api.post('/admin/stripe/prices', data),
  syncPricingPlans: () => api.post('/admin/stripe/sync-plans'),

  // Stripe Connect management
  listConnectAccounts: (params?: { limit?: number; startingAfter?: string }) =>
    api.get('/admin/stripe/connect/accounts', { params }),
  deleteConnectAccount: (accountId: string, options?: { forceDelete?: boolean }) =>
    api.delete(`/admin/stripe/connect/accounts/${accountId}`, { data: options }),
  disconnectOrgStripe: (organizationId: string, options?: {
    deleteFromStripe?: boolean;
    forceDelete?: boolean;
    reason?: string;
  }) => api.post(`/admin/organizations/${organizationId}/stripe/disconnect`, options),
};

// Billing API
export const billingApi = {
  getPlans: () => api.get('/billing/plans'),
  getSubscription: () => api.get('/billing/subscription'),
  createCheckout: (tier: string, interval: 'monthly' | 'yearly') =>
    api.post('/billing/checkout', { tier, interval }),
  getPortalSession: () => api.post('/billing/portal'),
  cancelSubscription: () => api.post('/billing/subscription/cancel'),
  resumeSubscription: () => api.post('/billing/subscription/resume'),
  changePlan: (tier: string, interval?: 'monthly' | 'yearly') =>
    api.post('/billing/subscription/change', { tier, interval }),
};

// Catalog API (Public)
export const catalogApi = {
  // Public browsing
  browse: (params?: { category?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/catalog', { params }),
  getListing: (listingId: string) => api.get(`/catalog/${listingId}`),
  getCategories: () => api.get('/catalog/categories'),
  getFeatured: () => api.get('/catalog/featured'),

  // Application submission
  apply: (listingId: string, data: {
    applicantName: string;
    applicantEmail: string;
    audienceDescription: string;
    websiteUrl?: string;
    socialLinks?: string[];
    monthlyVisitors?: number;
    promotionPlan?: string;
  }) => api.post(`/catalog/${listingId}/apply`, data),
};

// Organization Catalog Management API
export const orgCatalogApi = {
  getListing: () => api.get('/org/catalog/listing'),
  upsertListing: (data: {
    headline: string;
    description: string;
    categories: string[];
    commissionHighlight: string;
    cookieDuration: number;
    averageOrderValue?: number;
    conversionRate?: number;
    benefits: string[];
    applicationMode: 'auto_approve' | 'manual_review';
    applicationRequirements?: string;
    minAudienceSize?: number;
    allowedCountries?: string[];
    websiteUrl?: string;
  }) => api.put('/org/catalog/listing', data),
  deleteListing: () => api.delete('/org/catalog/listing'),
  upgradeListing: (tier: 'basic' | 'featured' | 'spotlight', billingInterval: 'monthly' | 'yearly') =>
    api.post('/org/catalog/listing/upgrade', { tier, billingInterval }),
  getStats: () => api.get('/org/catalog/listing/stats'),

  // Applications management
  listApplications: (params?: { status?: string }) =>
    api.get('/org/catalog/applications', { params }),
  getApplication: (applicationId: string) =>
    api.get(`/org/catalog/applications/${applicationId}`),
  reviewApplication: (applicationId: string, data: {
    action: 'approve' | 'reject';
    reviewNotes?: string;
    rejectionReason?: string;
  }) => api.put(`/org/catalog/applications/${applicationId}`, data),
};
